'use client'
import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import { useRef, useState } from 'react'
import { Tag } from '@/components/elementary/Tag'
import PricingCompareContent from '@/components/page-builder/global/pricing/PricingCompareContent'
import Heading from '@/components/typography/Heading'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { isRefSticky } from '@/hooks/useSticky'
import { cn } from '@/lib/styles'
import { PRICING_TEST_IDS } from '@/lib/test-ids'

export const PricingCompare = ({
	cardsData,
}: {
	cardsData?: Data.Component<'shared.pricing-item'>[] | null
}) => {
	const t = useTranslations('pricing')

	const [currentTab, setCurrentTab] = useState(cardsData?.[0]?.label ?? '')

	const stickyRef = useRef<HTMLDivElement>(null)
	const isStuck = isRefSticky(stickyRef, 64)
	return (
		<div className="mt-0 lg:mt-10 relative" data-test-id={PRICING_TEST_IDS.COMPARE}>
			<Heading tag="h2" fontSize="displayMedium" className="text-center w-full mb-2 lg:mb-10">
				{t('compareFeatures')}
			</Heading>
			<div className="w-full py-3 lg:hidden">
				<Tabs
					value={currentTab}
					onValueChange={setCurrentTab}
					data-test-id={PRICING_TEST_IDS.CONTENT_TAB}
				>
					<TabsList
						className={cn(
							'flex justify-center w-auto px-0 sticky z-20 top-16 pt-3 pb-0 h-auto rounded-none bg-neutral-light-100 -mx-6 ',
							isStuck && 'border-b border-brand-black',
						)}
						ref={stickyRef}
					>
						<div className="flex overflow-auto px-6 pb-3 w-auto ">
							{cardsData?.map((item) => (
								<TabsTrigger
									value={String(item?.label)}
									key={`${item?.id}-tab`}
									data-test-id={PRICING_TEST_IDS.CONTENT_TAB_TRIGGER}
								>
									{item?.label}
									{item?.isPopularTagVisible && (
										<Tag
											name={t('mostPopular')}
											size="small"
											className="w-fit text-sm lg:text-sm ml-2 -mr-2"
											fontWeight="font-bold"
											active
											customColors={{
												backgroundColor: 'neutral-light-100',
												borderColor: 'brand-black',
											}}
										/>
									)}
								</TabsTrigger>
							))}
						</div>
					</TabsList>
					{cardsData?.map((item) => (
						<TabsContent
							className="w-full"
							value={String(item?.label)}
							key={`${item?.id}-content`}
							data-test-id={PRICING_TEST_IDS.CONTENT_TAB_CONTENT}
						>
							<PricingCompareContent cardsData={cardsData} currentTab={item?.label} />
						</TabsContent>
					))}
				</Tabs>
			</div>
			<PricingCompareContent cardsData={cardsData} className="hidden lg:block" />
		</div>
	)
}

export default PricingCompare
